// Loan containers are both subject boundaries and screenshot boundaries. Canvas grids register a
// hit-test because a canvas has no DOM row ancestors; their screenshots are omitted (whole grid pixels).
import type { PageContext } from "./context";

export const LOAN_SELECTOR = "[data-loan-id], [data-core-loan-id]";
export const LOAN_LIST_ATTR = "data-feedback-loan-list";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export type Click = { clientX: number; clientY: number };
type GridBox = { x: number; y: number; width: number; height: number };
export type GridRect = GridBox & { visible?: GridBox };
export type GridHit = { loan_id: string; rect: GridRect };
type Grid = { hit: (click?: Click) => GridHit | null; lookup: (loan: string) => GridHit["rect"] | null };
const grids = new WeakMap<Element, Grid>();
export function registerFeedbackGrid(el: Element, hit: Grid["hit"], lookup: Grid["lookup"]) {
  grids.set(el, { hit, lookup });
}

/** Current rendered bounds for a stored subject; never use selection or the original click. */
export function feedbackGridRect(el: Element, loan: string): GridHit["rect"] | null {
  const grid = el.closest("[data-feedback-grid]");
  return grid ? grids.get(grid)?.lookup(loan) ?? null : null;
}

export function selectedSubject(el: Element, ctx: PageContext, click?: Click) {
  let boundary = el.closest(LOAN_SELECTOR);
  const grid = el.closest("[data-feedback-grid]");
  const hit = grid && (el.tagName === "CANVAS" || el.closest('[role="gridcell"]')) && grids.get(grid)?.hit(click);
  let loan = hit?.loan_id ?? boundary?.getAttribute("data-loan-id") ?? null;
  let core = boundary?.getAttribute("data-core-loan-id") ?? null;
  if ((loan && !UUID.test(loan)) || (core && !UUID.test(core)) || (loan && core)) {
    throw new Error("Open this loan before leaving feedback on it.");
  }
  loan = loan?.toLowerCase() ?? null;
  core = core?.toLowerCase() ?? null;
  // Containers with several subjects must never inherit just one of them.
  const subjects = new Set(Array.from(el.querySelectorAll(LOAN_SELECTOR)).map((n) =>
    `${n.getAttribute("data-loan-id") ?? ""}/${n.getAttribute("data-core-loan-id") ?? ""}`));
  const other = (loan || core) && [...subjects].some((s) => s !== `${loan ?? ""}/${core ?? ""}`);
  const many = subjects.size > 1 || !!other || !!el.closest(`[${LOAN_LIST_ATTR}]`) && !loan && !core;
  if (many) { loan = null; core = null; boundary = null; }
  if ((ctx.loan_id && ctx.loan_id !== loan) || (ctx.core_loan_id && ctx.core_loan_id !== core)) {
    // Query drawers can sit over a different loan or an entire board. Do not attach background
    // content to the open drawer's subject. Dedicated loan pages supply their own boundary too.
    throw new Error("Select content inside the open loan, or close it before pinning this area.");
  }
  if ((loan || core || many || !!el.querySelector(LOAN_SELECTOR)) && ctx.lo_person_id) {
    // The single-subject API cannot represent both a directory record and an embedded core deal.
    throw new Error("Open the loan page before leaving feedback on this deal.");
  }
  const context = { ...ctx, loan_id: loan, core_loan_id: core, lo_person_id: loan || core ? null : ctx.lo_person_id };
  return { context, boundary, gridHit: hit || null, loanList: many || !!el.querySelector(LOAN_SELECTOR) };
}

/** Translate actual grid bounds into one loan; clipped rows must not steal header clicks. */
export function hitTestLoanGrid(
  count: number,
  loanAt: (row: number) => string | null,
  bounds: (row?: number) => GridHit["rect"] | undefined,
  viewport: { left: number; right: number; top: number; bottom: number },
  activeRow: number,
  click?: Click,
): GridHit | null {
  const header = bounds();
  if (click && (click.clientX < viewport.left || click.clientX >= viewport.right
    || click.clientY < Math.max(viewport.top, (header?.y ?? viewport.top) + (header?.height ?? 0))
    || click.clientY >= viewport.bottom)) return null;
  let row = activeRow;
  if (click) {
    row = -1;
    for (let i = 0; i < count; i++) {
      const b = bounds(i);
      if (b && click.clientY >= b.y && click.clientY < b.y + b.height) { row = i; break; }
    }
  }
  const loan = row >= 0 && row < count ? loanAt(row) : null;
  const b = loan ? bounds(row) : undefined;
  if (!loan || !b) return null;
  const top = Math.max(b.y, viewport.top, (header?.y ?? viewport.top) + (header?.height ?? 0));
  const bottom = Math.min(b.y + b.height, viewport.bottom);
  return bottom > top && viewport.right > viewport.left
    ? { loan_id: loan, rect: {
      x: viewport.left, y: b.y, width: viewport.right - viewport.left, height: b.height,
      visible: { x: viewport.left, y: top, width: viewport.right - viewport.left, height: bottom - top },
    } }
    : null;
}
